import React, {useEffect, useCallback} from 'react';
import RegistrationForm from './RegistrationForm';
import trackAnalytics from '../utils/gaia/trackAnalytics';
import Helmet from 'react-helmet';
import {theme} from '../styles';
import {MuiThemeProvider} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import fetchUser from '../utils/gaia/fetchUser';
import Typography from '@material-ui/core/Typography';
import {GRANITE_3, GRANITE_1} from '../colors';
import gettext from '../utils/django/gettext';
import useMediaQuery from '@material-ui/core/useMediaQuery';

export default function HomePage({isMobile}: {isMobile: boolean}) {
    const nextPage = '/';
    const onUserAuthenticated = useCallback(() => {
        if (window) window.location.href = nextPage;
    }, [nextPage]);
    const isSideBySideVisible = useMediaQuery(theme.breakpoints.up('sm'));
    useEffect(() => {
        fetchUser().then((user) => {
            if (user.isAuthenticated) {
                window.location.href = nextPage;
            }
        }, console.error);

        trackAnalytics('Viewed Home Page', {url: window.location.href});

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line max-len
    const homeMetaDesc =
        'Plan trips and record GPS tracks for free, with the best outdoor app. Browse hiking trail maps, hunting units, and scout offroad camping trips. Buy a membership to download maps, including topos, aerial imagery, public/private land ownership, and National Geographic.';
    return (
        <>
            <Helmet>
                <title>
                    Hiking Trail Maps, Hunting Units, 4x4 Offroad App | Gaia GPS
                </title>
                <meta property='description' content={homeMetaDesc} />
                <meta name='revisit-after' content='1 day' />
                <meta property='og:site_name' content='GaiaGPS.com' />
                <link rel='canonical' href='https://www.gaiagps.com' />
                <link rel='manifest' href='/static/manifest.json' />
                <meta property='og:url' content='https://www.gaiagps.com' />
                <meta
                    property='og:title'
                    content='Gaia GPS: Navigate, Track, and Explore'
                />
                <meta property='og:description' content={homeMetaDesc} />
                <meta
                    property='og:image'
                    content='https://www.gaiagps.com/static/img/logos_icons/gaia_gps_logo.svg'
                />
                <meta
                    property='twitter:title'
                    content='https://www.gaiagps.com/static/img/logos_icons/gaia_gps_logo.svg'
                />
                <meta property='twitter:site' content='@gaiagps' />
                <meta property='twitter:description' content={homeMetaDesc} />
                <meta property='twitter:card' content='summary_large_image' />
                <meta
                    property='twitter:image:src'
                    content='https://www.gaiagps.com/static/img/logos_icons/gaia_gps_logo.svg'
                />
                <script type='application/ld+json'>
                    {JSON.stringify({
                        '@context': 'http://schema.org/',
                        '@type': 'Organization',
                        '@id': 'https://www.gaiagps.com/company/',
                        name: 'TrailBehind, Inc.',
                        foundingDate: '2008',
                        brand: {
                            '@type': 'Brand',
                            '@id': 'https://www.gaiagps.com/',
                            name: 'Gaia GPS',
                            logo:
                                'https://www.gaiagps.com/static/img/logos_icons/gaia-gps_logo.png',
                        },
                        founder: [
                            {
                                '@type': 'Person',
                                givenName: 'Andrew',
                                familyName: 'Johnson',
                                jobTitle: 'CEO',
                            },
                            {
                                '@type': 'Person',
                                givenName: 'Anna',
                                familyName: 'Johnson',
                                jobTitle: 'CTO',
                            },
                            {
                                '@type': 'Person',
                                givenName: 'Jesse',
                                familyName: 'Crocker',
                                jobTitle: 'Engineering Manager',
                            },
                        ],
                    })}
                </script>
            </Helmet>
            <MuiThemeProvider theme={theme}>
                <div
                    style={{
                        marginTop: theme.spacing(3),
                        marginBottom: theme.spacing(5),
                    }}
                >
                    <Typography variant='h2' align='center'>
                        {gettext('Find Your Adventure')}
                    </Typography>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {isSideBySideVisible && (
                        <img
                            style={{
                                width: '40%',
                                marginRight: theme.spacing(2),
                                marginBottom: theme.spacing(15),
                            }}
                            src='/static/img/device-collage-4.png'
                            // eslint-disable-next-line max-len
                            alt='Gaia GPS is available for your desktop computer, mobile phone, tablet, Apple Watch and CarPlay enabled devices.'
                        />
                    )}
                    <Paper
                        style={{
                            padding: theme.spacing(5),
                            marginBottom: theme.spacing(15),
                        }}
                    >
                        {isMobile ? (
                            <AppIcons />
                        ) : (
                            <RegistrationForm
                                nextPage={nextPage}
                                onUserAuthenticated={onUserAuthenticated}
                            />
                        )}
                    </Paper>
                </div>
            </MuiThemeProvider>
        </>
    );
}

function AppIcons() {
    const style = {
        fontSize: 14,
        outline: 0,
        height: 34,
    };

    return (
        <>
            <div style={{maxWidth: 275, marginBottom: theme.spacing(2)}}>
                <Typography
                    variant='h4'
                    align='center'
                    style={{
                        fontWeight: 'bold',
                        color: GRANITE_1,
                    }}
                >
                    {gettext('Download the app')}
                </Typography>
                <br />
                <Typography
                    variant='subtitle1'
                    align='center'
                    style={{color: GRANITE_3}}
                >
                    Get the Gaia GPS app on your smartphone or tablet and start
                    exploring today.
                </Typography>
            </div>
            <div style={{textAlign: 'center'}}>
                <a
                    href='https://itunes.apple.com/us/app/gaia-gps/id1201979492?mt=8&uo=4'
                    target='itunes_store'
                    className='hide-if-android'
                    onClick={() =>
                        trackAnalytics('Buy iOS', {
                            category: 'Commerce',
                            label: 'Home Page',
                            page: window.location.href,
                        })
                    }
                >
                    <img
                        style={style}
                        src='static/img/appstore.svg'
                        alt='Download on the App Store'
                    />
                </a>

                <a
                    // eslint-disable-next-line max-len
                    href='https://market.android.com/details?id=com.trailbehind.android.gaiagps.pro&hl=en'
                    target='_blank'
                    className='hide-if-ios'
                    rel='noopener noreferrer'
                    onClick={() =>
                        trackAnalytics('Buy Android', {
                            category: 'Commerce',
                            label: 'Home Page',
                            page: window.location.href,
                        })
                    }
                >
                    <img
                        style={style}
                        src='static/img/icons/play_badge.png'
                        alt='Get it on Google Play'
                    />
                </a>
            </div>
        </>
    );
}
